// src/App.js

import React, { useMemo, useState, useEffect } from "react";
import { leagues } from "./data/leagueData";

// --- MODERN 2026 DESIGN TOKENS ---
const TOKENS = {
  bg: "#09090b",
  card: "rgba(255, 255, 255, 0.03)",
  cardBorder: "rgba(255, 255, 255, 0.08)",
  text: "#fafafa",
  textMuted: "#a1a1aa",
  accent: "#3b82f6",
  success: "#10b981",
  danger: "#ef4444",
  gold: "linear-gradient(to right, #facc15, #a16207)",
  spotlightBg:
    "linear-gradient(135deg, rgba(251, 191, 36, 0.15) 0%, rgba(0,0,0,0) 60%)",
};

// --- HELPER FUNCTIONS ---
function money(n) {
  if (n == null || Number.isNaN(n)) return "—";
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function getTeamById(league, id) {
  return (
    league.teams.find((t) => t.id === id) || {
      name: "Unknown",
      owner: "Unknown",
    }
  );
}

function getTeamBySeed(league, seed) {
  return league.teams.find((t) => t.seed === seed) || null;
}

// --- COMPONENTS ---

function PotBreakdown({ league }) {
  const { financials } = league;
  const p = financials.payouts;
  return (
    <div style={{ marginBottom: 32 }}>
      <h3
        style={{
          margin: "0 0 16px 0",
          fontSize: 20,
          fontWeight: 800,
          color: TOKENS.text,
        }}
      >
        League Economy
      </h3>
      <div
        style={{
          background: TOKENS.card,
          border: `1px solid ${TOKENS.cardBorder}`,
          borderRadius: 16,
          padding: 24,
        }}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 20,
            borderBottom: `1px solid ${TOKENS.cardBorder}`,
            paddingBottom: 20,
            marginBottom: 20,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                background: "rgba(59, 130, 246, 0.15)",
                color: TOKENS.accent,
                fontWeight: 800,
                padding: "8px 12px",
                borderRadius: 8,
              }}
            >
              10 Teams
            </div>
            <div style={{ color: TOKENS.textMuted }}>×</div>
            <div
              style={{
                background: "rgba(59, 130, 246, 0.15)",
                color: TOKENS.accent,
                fontWeight: 800,
                padding: "8px 12px",
                borderRadius: 8,
              }}
            >
              $500 Entry
            </div>
          </div>
          <div style={{ fontSize: 28, fontWeight: 900, color: TOKENS.success }}>
            {money(financials.totalPot)} Total Pot
          </div>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 16,
          }}
        >
          {[
            {
              label: "Weekly Pool",
              val: p.weekly.amount,
              note: "$85.71 × 14 weeks",
            },
            {
              label: "Reg. Season Champ",
              val: p.regularSeason.amount,
              note: "Best Record",
            },
            {
              label: "Runner Up",
              val: p.runnerUp.amount,
              note: "Lost in Finals",
            },
            {
              label: "League Champion",
              val: p.champion.amount,
              note: "Won Finals",
              highlight: true,
            },
          ].map((item, i) => (
            <div
              key={i}
              style={{
                padding: 16,
                background: item.highlight
                  ? "rgba(251, 191, 36, 0.05)"
                  : "rgba(255,255,255,0.03)",
                borderRadius: 12,
                border: `1px solid ${
                  item.highlight ? "rgba(251, 191, 36, 0.3)" : TOKENS.cardBorder
                }`,
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  color: item.highlight ? "#fbbf24" : TOKENS.textMuted,
                  textTransform: "uppercase",
                  fontWeight: 700,
                }}
              >
                {item.label}
              </div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 800,
                  color: TOKENS.text,
                  margin: "4px 0",
                }}
              >
                {money(item.val)}
              </div>
              <div style={{ fontSize: 11, color: TOKENS.textMuted }}>
                {item.note}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CoachFinancials({ league }) {
  const [selectedOwnerId, setSelectedOwnerId] = useState(league.teams[0].id);
  const selectedTeam = getTeamById(league, selectedOwnerId);
  const entryFee = league.financials.entryFee;
  const weeklyPool = league.financials.payouts.weekly.amount;
  const payoutPerWeek = weeklyPool / 14;

  const winnings = [];
  league.weeks.forEach((w) => {
    if (w.week <= 14 && w.highScore?.teamId === selectedOwnerId) {
      winnings.push({
        label: `Week ${w.week} High Score`,
        amount: payoutPerWeek,
      });
    }
  });

  const { payouts } = league.financials;
  // Multi-finish logic to handle Hurts Donut taking both titles
  if (selectedTeam.finish?.includes("regular_season_champ")) {
    winnings.push({
      label: "Regular Season Champ",
      amount: payouts.regularSeason.amount,
    });
  }
  if (selectedTeam.finish?.includes("champion")) {
    winnings.push({
      label: "League Champion",
      amount: payouts.champion.amount,
    });
  }
  if (selectedTeam.finish?.includes("runner_up")) {
    winnings.push({ label: "Runner Up", amount: payouts.runnerUp.amount });
  }

  const totalWon = winnings.reduce((sum, item) => sum + item.amount, 0);
  const netTotal = entryFee - totalWon;
  const isOwedMoney = netTotal < 0;

  return (
    <div style={{ marginBottom: 40 }}>
      <h3 style={{ margin: "0 0 16px 0", fontSize: 20, fontWeight: 800 }}>
        Coach Portal
      </h3>
      <div
        style={{
          display: "flex",
          gap: 8,
          overflowX: "auto",
          paddingBottom: 12,
          marginBottom: 8,
        }}
      >
        {league.teams.map((t) => (
          <button
            key={t.id}
            onClick={() => setSelectedOwnerId(t.id)}
            style={{
              background:
                selectedOwnerId === t.id
                  ? TOKENS.accent
                  : "rgba(255,255,255,0.05)",
              color: "white",
              border: "none",
              borderRadius: 8,
              padding: "8px 16px",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            {t.name}
          </button>
        ))}
      </div>
      <div
        style={{
          background: TOKENS.card,
          border: `1px solid ${TOKENS.cardBorder}`,
          borderRadius: 16,
          padding: 24,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 20,
          }}
        >
          <div>
            <div style={{ fontSize: 24, fontWeight: 800 }}>
              {selectedTeam.name}
            </div>
            <div style={{ color: TOKENS.textMuted }}>
              Coach: {selectedTeam.owner}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div
              style={{ fontSize: 12, fontWeight: 700, color: TOKENS.textMuted }}
            >
              STATUS
            </div>
            <div
              style={{
                fontSize: 22,
                fontWeight: 900,
                color: isOwedMoney ? TOKENS.success : "white",
              }}
            >
              {isOwedMoney ? "PAYOUT" : "DUE"}
            </div>
          </div>
        </div>
        <div style={{ display: "grid", gap: 12 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              paddingBottom: 12,
              borderBottom: `1px solid ${TOKENS.cardBorder}`,
            }}
          >
            <span>League Entry Fee</span>
            <span>{money(entryFee)}</span>
          </div>
          {winnings.map((win, idx) => (
            <div
              key={idx}
              style={{
                display: "flex",
                justifyContent: "space-between",
                color: TOKENS.success,
              }}
            >
              <span>{win.label}</span>
              <span>- {money(win.amount)}</span>
            </div>
          ))}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              paddingTop: 16,
              borderTop: `1px solid ${TOKENS.cardBorder}`,
              fontSize: 18,
              fontWeight: 800,
            }}
          >
            <span>Total {isOwedMoney ? "Payout" : "Due"}</span>
            <span>{money(Math.abs(netTotal))}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function BracketView({ league }) {
  if (!league.playoffs) return null;

  const resolveTeam = (match, side) => {
    const isHome = side === "home";
    const seed = isHome ? match.homeSeed : match.awaySeed;
    const from = isHome ? match.homeFrom : match.awayFrom;

    if (seed) return getTeamBySeed(league, seed);
    // Hardcoded logic for path based on final results
    if (from === "sf1") return getTeamBySeed(league, 1);
    if (from === "sf2") return getTeamBySeed(league, 6);
    if (from === "qf1") return getTeamBySeed(league, 4);
    if (from === "qf2") return getTeamBySeed(league, 6);
    return { name: "TBD" };
  };

  return (
    <div
      style={{
        background: TOKENS.card,
        border: `1px solid ${TOKENS.cardBorder}`,
        borderRadius: 16,
        padding: 20,
      }}
    >
      <h3 style={{ margin: "0 0 16px 0", fontSize: 18 }}>Playoff Bracket</h3>
      <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        {league.playoffs.rounds.map((round) => (
          <div key={round.id}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 800,
                color: TOKENS.textMuted,
                textTransform: "uppercase",
                marginBottom: 8,
              }}
            >
              {round.label}
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              {round.matchups.map((m) => {
                const home = resolveTeam(m, "home");
                const away = resolveTeam(m, "away");
                const winnerId = m.winnerSeed
                  ? getTeamBySeed(league, m.winnerSeed)?.id
                  : null;
                return (
                  <div
                    key={m.id}
                    style={{
                      background: "rgba(0,0,0,0.2)",
                      border: `1px solid ${TOKENS.cardBorder}`,
                      borderRadius: 10,
                      padding: 12,
                    }}
                  >
                    {m.label && (
                      <div
                        style={{
                          fontSize: 10,
                          color: TOKENS.accent,
                          fontWeight: 700,
                          marginBottom: 4,
                        }}
                      >
                        {m.label}
                      </div>
                    )}
                    <TeamRow
                      team={home}
                      score={m.homeScore}
                      isWinner={winnerId === home?.id}
                    />
                    <div
                      style={{
                        height: 1,
                        background: TOKENS.cardBorder,
                        margin: "8px 0",
                      }}
                    />
                    <TeamRow
                      team={away}
                      score={m.awayScore}
                      isWinner={winnerId === away?.id}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const TeamRow = ({ team, score, isWinner }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      opacity: isWinner || score === undefined ? 1 : 0.5,
    }}
  >
    <span
      style={{
        fontSize: 13,
        fontWeight: isWinner ? 700 : 400,
        color: isWinner ? TOKENS.success : TOKENS.text,
      }}
    >
      {team?.name || "TBD"}
    </span>
    <span
      style={{
        fontSize: 12,
        fontWeight: 700,
        color: isWinner ? TOKENS.success : TOKENS.textMuted,
      }}
    >
      {score || ""}
    </span>
  </div>
);

function WinnersPodium({ league }) {
  const { payouts } = league.financials;
  const champion = league.teams.find((t) => t.finish?.includes("champion"));
  const regSeason = league.teams.find((t) =>
    t.finish?.includes("regular_season_champ")
  );
  const runnerUp = league.teams.find((t) => t.finish?.includes("runner_up"));
  const pod = [
    {
      label: "League Champion",
      val: payouts.champion.amount,
      team: champion,
      color: TOKENS.gold,
    },
    {
      label: "Reg. Season Best",
      val: payouts.regularSeason.amount,
      team: regSeason,
    },
    { label: "Runner Up", val: payouts.runnerUp.amount, team: runnerUp },
  ];
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
        gap: 16,
        marginBottom: 32,
      }}
    >
      {pod.map((item, i) => (
        <div
          key={i}
          style={{
            background: TOKENS.card,
            border: `1px solid ${TOKENS.cardBorder}`,
            borderRadius: 16,
            padding: 20,
            position: "relative",
            overflow: "hidden",
          }}
        >
          {item.color && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: item.color,
              }}
            />
          )}
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: TOKENS.textMuted,
              textTransform: "uppercase",
            }}
          >
            {item.label}
          </div>
          <div style={{ fontSize: 32, fontWeight: 800, margin: "8px 0" }}>
            {money(item.val)}
          </div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>
            {item.team ? item.team.name : "TBD"}
          </div>
        </div>
      ))}
    </div>
  );
}

function StandingsTable({ league }) {
  const sorted = [...league.teams].sort(
    (a, b) => (a.seed || 99) - (b.seed || 99)
  );
  return (
    <div
      style={{
        background: TOKENS.card,
        border: `1px solid ${TOKENS.cardBorder}`,
        borderRadius: 16,
        padding: 20,
      }}
    >
      <h3 style={{ margin: "0 0 16px 0", fontSize: 18 }}>Final Standings</h3>
      <div style={{ display: "grid", gap: 8 }}>
        {sorted.map((t) => (
          <div
            key={t.id}
            style={{
              display: "grid",
              gridTemplateColumns: "30px 1fr 1fr auto",
              alignItems: "center",
              padding: 10,
              borderRadius: 8,
              background: t.madePlayoffs
                ? "rgba(16, 185, 129, 0.05)"
                : "transparent",
            }}
          >
            <div
              style={{ fontSize: 12, fontWeight: 700, color: TOKENS.textMuted }}
            >
              {t.seed}
            </div>
            <div style={{ fontWeight: 600 }}>{t.name}</div>
            <div style={{ fontSize: 13, color: TOKENS.textMuted }}>
              {t.owner}
            </div>
            {t.madePlayoffs && (
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 800,
                  color: TOKENS.success,
                  textTransform: "uppercase",
                }}
              >
                Playoffs
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function App() {
  const [activeTab, setActiveTab] = useState("current10");
  const league = leagues[activeTab];

  return (
    <div
      style={{
        minHeight: "100vh",
        background: TOKENS.bg,
        color: TOKENS.text,
        fontFamily: "system-ui, sans-serif",
        paddingBottom: 60,
      }}
    >
      <header
        style={{
          borderBottom: `1px solid ${TOKENS.cardBorder}`,
          background: "rgba(9,9,11,0.8)",
          backdropFilter: "blur(12px)",
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            padding: "16px 20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div
            style={{
              fontWeight: 900,
              fontSize: 24,
              background: TOKENS.gold,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            LEGENDS FANTASY LEAGUE
          </div>
        </div>
      </header>
      <main style={{ maxWidth: 1200, margin: "32px auto", padding: "0 20px" }}>
        <PotBreakdown league={league} />
        <WinnersPodium league={league} />
        <CoachFinancials league={league} />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))",
            gap: 24,
          }}
        >
          <StandingsTable league={league} />
          <BracketView league={league} />
        </div>
      </main>
    </div>
  );
}
